/**
 * useWallState - Wall volume body state management
 *
 * Manages walls as volume bodies (8 points each) built on top of
 * geometry baselines from useGeometryState.
 *
 * A wall references a geometry curve (its inner edge / baseline).
 * From baseline + thickness + height, 8 corner points are derived.
 * Joints between walls modify corner positions at connection ends.
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import { Vector } from '../../geometry/base/Vector';
import {
  JointType,
  calculateWallPoints,
  calculateButtJoint,
  calculateMiterJoint,
  detectWallConnections,
  wallNormal,
} from '../../building/WallJoint';

let wallIdCounter = 0;
function generateWallId() {
  return `wall_${Date.now()}_${++wallIdCounter}`;
}

let connectionIdCounter = 0;
function generateConnectionId() {
  return `conn_${Date.now()}_${++connectionIdCounter}`;
}

/**
 * Create a wall volume from a geometry curve.
 * @param {Object} curve - Geometry curve object (Line or Polyline segment)
 * @param {number} thickness
 * @param {number} height
 * @param {boolean} flipped
 * @returns {Object} Wall data
 */
function createWallData(curve, thickness = 240, height = 2750, flipped = false) {
  // Extract baseline start/end from curve
  let baseStart, baseEnd;

  if (curve.start && curve.end) {
    // Line
    baseStart = curve.start instanceof Vector ? curve.start : Vector.fromObject(curve.start);
    baseEnd = curve.end instanceof Vector ? curve.end : Vector.fromObject(curve.end);
  } else if (curve.startPoint && curve.endPoint) {
    baseStart = curve.startPoint instanceof Vector ? curve.startPoint : Vector.fromObject(curve.startPoint);
    baseEnd = curve.endPoint instanceof Vector ? curve.endPoint : Vector.fromObject(curve.endPoint);
  } else {
    throw new Error('Curve must have start/end or startPoint/endPoint');
  }

  const points = calculateWallPoints(baseStart, baseEnd, thickness, height, flipped);

  return {
    id: generateWallId(),
    baselineCurveId: curve.id,
    thickness,
    height,
    flipped,
    baseStart: baseStart.toObject(),
    baseEnd: baseEnd.toObject(),
    points: {
      innerBottomStart: points.innerBottomStart.toObject(),
      innerBottomEnd: points.innerBottomEnd.toObject(),
      outerBottomStart: points.outerBottomStart.toObject(),
      outerBottomEnd: points.outerBottomEnd.toObject(),
      innerTopStart: points.innerTopStart.toObject(),
      innerTopEnd: points.innerTopEnd.toObject(),
      outerTopStart: points.outerTopStart.toObject(),
      outerTopEnd: points.outerTopEnd.toObject(),
    },
    connections: {
      start: null, // { wallId, jointType }
      end: null,
    },
    openings: [],
    typeRef: null,
    layerSetId: null,
    createdAt: Date.now(),
    modifiedAt: Date.now(),
  };
}

/**
 * Recalculate a wall's 8 points from its baseline + thickness + height.
 * Does NOT apply joints — call applyJoints separately.
 */
function recalcWallPoints(wall) {
  const baseStart = Vector.fromObject(wall.baseStart);
  const baseEnd = Vector.fromObject(wall.baseEnd);
  const pts = calculateWallPoints(baseStart, baseEnd, wall.thickness, wall.height, wall.flipped);

  return {
    ...wall,
    points: {
      innerBottomStart: pts.innerBottomStart.toObject(),
      innerBottomEnd: pts.innerBottomEnd.toObject(),
      outerBottomStart: pts.outerBottomStart.toObject(),
      outerBottomEnd: pts.outerBottomEnd.toObject(),
      innerTopStart: pts.innerTopStart.toObject(),
      innerTopEnd: pts.innerTopEnd.toObject(),
      outerTopStart: pts.outerTopStart.toObject(),
      outerTopEnd: pts.outerTopEnd.toObject(),
    },
    modifiedAt: Date.now(),
  };
}

/**
 * Apply a joint result to a wall at the given end.
 * @param {Object} wall
 * @param {'start'|'end'} end
 * @param {Object} jointPoints - { innerBottom, outerBottom, innerTop, outerTop }
 * @returns {Object} Updated wall
 */
function applyJointPoints(wall, end, jointPoints) {
  const suffix = end === 'start' ? 'Start' : 'End';
  return {
    ...wall,
    points: {
      ...wall.points,
      [`innerBottom${suffix}`]: jointPoints.innerBottom.toObject(),
      [`outerBottom${suffix}`]: jointPoints.outerBottom.toObject(),
      [`innerTop${suffix}`]: jointPoints.innerTop.toObject(),
      [`outerTop${suffix}`]: jointPoints.outerTop.toObject(),
    },
    modifiedAt: Date.now(),
  };
}

/**
 * Hook for managing wall volume state
 * @param {Object} geometryState - Reference to useGeometryState for curve lookups
 * @param {Object} options
 * @returns {Object} Wall state and operations
 */
export function useWallState(geometryState = null) {
  const [walls, setWalls] = useState([]);
  const [connections, setConnections] = useState([]);

  // Keep geometry state ref for callbacks
  const geoRef = useRef(geometryState);
  geoRef.current = geometryState;

  // ============= HELPERS =============

  /**
   * Get a geometry curve by ID from geometry state
   */
  const getCurve = useCallback((curveId) => {
    const geo = geoRef.current;
    if (!geo) return null;
    const curves = geo.curves || geo.geometry?.curves || [];
    return curves.find(c => c.id === curveId) || null;
  }, []);

  /**
   * Convert wall data to the format WallJoint functions expect
   */
  const toJointFormat = useCallback((wall) => ({
    id: wall.id,
    baseStart: Vector.fromObject(wall.baseStart),
    baseEnd: Vector.fromObject(wall.baseEnd),
    thickness: wall.thickness,
    height: wall.height,
    flipped: wall.flipped,
  }), []);

  // ============= WALL OPERATIONS =============

  /**
   * Create a wall from a geometry curve
   * @param {string} curveId - ID of the baseline curve (inner edge)
   * @param {Object} options - { thickness, height, flipped, typeRef, layerSetId }
   * @returns {Object} Created wall data
   */
  const createWallFromCurve = useCallback((curveId, options = {}) => {
    const curve = getCurve(curveId);
    if (!curve) {
      console.warn(`useWallState: Curve ${curveId} not found`);
      return null;
    }

    const {
      thickness = 240,
      height = 2750,
      flipped = false,
      typeRef = null,
      layerSetId = null,
    } = options;

    const wall = createWallData(curve, thickness, height, flipped);
    wall.typeRef = typeRef;
    wall.layerSetId = layerSetId;

    setWalls(prev => [...prev, wall]);

    // Auto-detect connections with existing walls
    setWalls(prevWalls => {
      const allWalls = [...prevWalls];
      const newWall = allWalls[allWalls.length - 1];
      const newConnections = detectWallConnections(
        allWalls.map(w => toJointFormat(w)),
        5 // 5mm tolerance
      );

      // Update connections state
      if (newConnections.length > 0) {
        setConnections(prev => {
          // Remove existing connections for this wall
          const filtered = prev.filter(
            c => c.wall1Id !== newWall.id && c.wall2Id !== newWall.id
          );
          return [...filtered, ...newConnections.map(c => ({
            ...c,
            id: generateConnectionId(),
          }))];
        });
      }

      return allWalls;
    });

    return wall;
  }, [getCurve, toJointFormat]);

  /**
   * Update wall properties (thickness, height, typeRef, etc.)
   * Recalculates 8 points automatically.
   */
  const updateWall = useCallback((wallId, updates) => {
    setWalls(prev => prev.map(w => {
      if (w.id !== wallId) return w;
      const updated = { ...w, ...updates, modifiedAt: Date.now() };
      // If baseline-affecting props changed, recalculate points
      if ('thickness' in updates || 'height' in updates || 'flipped' in updates) {
        return recalcWallPoints(updated);
      }
      return updated;
    }));
  }, []);

  /**
   * Move a wall's baseline point (start or end).
   * Preserves thickness — recalculates outer points.
   * @param {string} wallId
   * @param {'start'|'end'} whichEnd
   * @param {number} x
   * @param {number} y
   */
  const moveWallBaselinePoint = useCallback((wallId, whichEnd, x, y) => {
    setWalls(prev => prev.map(w => {
      if (w.id !== wallId) return w;
      const updated = {
        ...w,
        [whichEnd === 'start' ? 'baseStart' : 'baseEnd']: { x, y, z: 0 },
      };
      return recalcWallPoints(updated);
    }));
  }, []);

  /**
   * Flip wall inner/outer side
   */
  const flipWall = useCallback((wallId) => {
    setWalls(prev => prev.map(w => {
      if (w.id !== wallId) return w;
      const updated = { ...w, flipped: !w.flipped };
      return recalcWallPoints(updated);
    }));
  }, []);

  /**
   * Delete a wall
   */
  const deleteWall = useCallback((wallId) => {
    setWalls(prev => prev.filter(w => w.id !== wallId));
    setConnections(prev => prev.filter(
      c => c.wall1Id !== wallId && c.wall2Id !== wallId
    ));
  }, []);

  /**
   * Set joint type for a connection
   * @param {string} connectionId
   * @param {string} jointType - 'butt' | 'miter' | 'none'
   */
  const setJointType = useCallback((connectionId, jointType) => {
    setConnections(prev => prev.map(c =>
      c.id === connectionId ? { ...c, jointType } : c
    ));
  }, []);

  /**
   * Apply all joints to wall points.
   * Call after any wall geometry changes.
   */
  const applyAllJoints = useCallback(() => {
    setWalls(prev => {
      // First recalculate all base points (without joints)
      let updated = prev.map(w => recalcWallPoints(w));

      // Then apply each connection's joint
      for (const conn of connections) {
        const w1Idx = updated.findIndex(w => w.id === conn.wall1Id);
        const w2Idx = updated.findIndex(w => w.id === conn.wall2Id);
        if (w1Idx === -1 || w2Idx === -1) continue;

        const w1 = updated[w1Idx];
        const w2 = updated[w2Idx];
        const jw1 = toJointFormat(w1);
        const jw2 = toJointFormat(w2);

        if (conn.jointType === JointType.BUTT) {
          // Trim wall1 against wall2's inner face
          const result = calculateButtJoint(jw1, jw2, conn.wall1End);
          if (result) {
            updated = [...updated];
            updated[w1Idx] = applyJointPoints(w1, conn.wall1End, result);
          }
        } else if (conn.jointType === JointType.MITER) {
          const result = calculateMiterJoint(jw1, jw2, conn.wall1End, conn.wall2End);
          if (result) {
            updated = [...updated];
            updated[w1Idx] = applyJointPoints(w1, conn.wall1End, result.wall1Points);
            updated[w2Idx] = applyJointPoints(w2, conn.wall2End, result.wall2Points);
          }
        }
        // JointType.NONE: do nothing
      }

      return updated;
    });
  }, [connections, toJointFormat]);

  // ============= OPENINGS =============

  /**
   * Add opening (window/door) to a wall
   */
  const addOpening = useCallback((wallId, opening) => {
    const id = `opening_${Date.now()}_${++wallIdCounter}`;
    setWalls(prev => prev.map(w => {
      if (w.id !== wallId) return w;
      return {
        ...w,
        openings: [...w.openings, {
          id,
          type: opening.type || 'window',
          position: opening.position ?? 0.5,
          width: opening.width || 1200,
          height: opening.height || 1400,
          sillHeight: opening.sillHeight ?? (opening.type === 'door' ? 0 : 900),
          ...opening,
          id, // ensure id is set
        }],
        modifiedAt: Date.now(),
      };
    }));
    return id;
  }, []);

  /**
   * Update an opening
   */
  const updateOpening = useCallback((wallId, openingId, updates) => {
    setWalls(prev => prev.map(w => {
      if (w.id !== wallId) return w;
      return {
        ...w,
        openings: w.openings.map(o =>
          o.id === openingId ? { ...o, ...updates } : o
        ),
        modifiedAt: Date.now(),
      };
    }));
  }, []);

  /**
   * Remove an opening
   */
  const removeOpening = useCallback((wallId, openingId) => {
    setWalls(prev => prev.map(w => {
      if (w.id !== wallId) return w;
      return {
        ...w,
        openings: w.openings.filter(o => o.id !== openingId),
        modifiedAt: Date.now(),
      };
    }));
  }, []);

  // ============= SYNC WITH GEOMETRY =============

  /**
   * Sync wall baselines from geometry state.
   * Call when geometry points/curves are moved.
   * Updates baseStart/baseEnd from the referenced curve, then recalculates 8 points.
   */
  const syncFromGeometry = useCallback(() => {
    setWalls(prev => prev.map(w => {
      const curve = getCurve(w.baselineCurveId);
      if (!curve) return w;

      let baseStart, baseEnd;
      if (curve.start && curve.end) {
        baseStart = curve.start instanceof Vector ? curve.start.toObject() : { x: curve.start.x, y: curve.start.y, z: 0 };
        baseEnd = curve.end instanceof Vector ? curve.end.toObject() : { x: curve.end.x, y: curve.end.y, z: 0 };
      } else if (curve.startPoint && curve.endPoint) {
        const sp = curve.startPoint;
        const ep = curve.endPoint;
        baseStart = { x: sp.x, y: sp.y, z: 0 };
        baseEnd = { x: ep.x, y: ep.y, z: 0 };
      } else {
        return w;
      }

      // Only update if position actually changed
      if (baseStart.x === w.baseStart.x && baseStart.y === w.baseStart.y &&
          baseEnd.x === w.baseEnd.x && baseEnd.y === w.baseEnd.y) {
        return w;
      }

      const updated = { ...w, baseStart, baseEnd };
      return recalcWallPoints(updated);
    }));
  }, [getCurve]);

  // ============= QUERIES =============

  /**
   * Get wall by ID
   */
  const getWall = useCallback((id) => {
    return walls.find(w => w.id === id) || null;
  }, [walls]);

  /**
   * Get walls connected to a specific wall
   */
  const getConnectedWalls = useCallback((wallId) => {
    return connections.filter(c => c.wall1Id === wallId || c.wall2Id === wallId);
  }, [connections]);

  /**
   * Get connections at a specific wall end
   */
  const getConnectionAt = useCallback((wallId, end) => {
    return connections.find(c =>
      (c.wall1Id === wallId && c.wall1End === end) ||
      (c.wall2Id === wallId && c.wall2End === end)
    ) || null;
  }, [connections]);

  // ============= QUANTITIES =============

  /**
   * Calculate wall quantities
   */
  const wallQuantities = useMemo(() => {
    return walls.map(w => {
      const baseStart = Vector.fromObject(w.baseStart);
      const baseEnd = Vector.fromObject(w.baseEnd);
      const length = baseStart.distanceTo(baseEnd);
      const grossArea = length * w.height;
      const openingArea = w.openings.reduce((sum, o) => sum + o.width * o.height, 0);
      const netArea = Math.max(0, grossArea - openingArea);

      return {
        wallId: w.id,
        length,              // mm
        height: w.height,    // mm
        thickness: w.thickness, // mm
        grossArea,           // mm²
        openingArea,         // mm²
        netArea,             // mm²
        volume: netArea * w.thickness, // mm³
        // Convert to m/m²/m³
        lengthM: length / 1000,
        grossAreaM2: grossArea / 1e6,
        openingAreaM2: openingArea / 1e6,
        netAreaM2: netArea / 1e6,
        volumeM3: (netArea * w.thickness) / 1e9,
      };
    });
  }, [walls]);

  // ============= SERIALIZATION =============

  const toJSON = useCallback(() => ({
    walls: walls.map(w => ({ ...w })),
    connections: connections.map(c => ({ ...c })),
  }), [walls, connections]);

  const fromJSON = useCallback((json) => {
    if (json.walls) setWalls(json.walls);
    if (json.connections) setConnections(json.connections);
  }, []);

  const clear = useCallback(() => {
    setWalls([]);
    setConnections([]);
  }, []);

  return {
    // State
    walls,
    connections,

    // Wall CRUD
    createWallFromCurve,
    updateWall,
    deleteWall,
    getWall,
    flipWall,

    // Point manipulation
    moveWallBaselinePoint,

    // Joints
    setJointType,
    applyAllJoints,
    getConnectedWalls,
    getConnectionAt,

    // Openings
    addOpening,
    updateOpening,
    removeOpening,

    // Sync
    syncFromGeometry,

    // Quantities
    wallQuantities,

    // Serialization
    toJSON,
    fromJSON,
    clear,
  };
}

export default useWallState;
